"use client";
import ActiveLink from "@/components/active-link";
import InputField from "@/components/form/input-field";
import ThemeSwitcher from "@/components/theme-switcher";
import { asMenuItem, getNestedMenu, useMenu } from "@/context/menu-context";
import { useSidebar } from "@/context/sidebar-context";
import { Content } from "@/services/content/types";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";
import { CustomCategory } from "../../category";
import Icon from "../../icon";
import SidebarSettings from "./sidebar-settings";

const resolveHref = (menuItem: Content.MenuItem): string => {
  const slug = menuItem.fields.item?.fields?.slug;
  if (slug === "index") return "/";
  if (slug) return slug.startsWith("/") ? slug : `/${slug}`;
  if (menuItem.fields.url) return menuItem.fields.url;
  return "#";
};

const SidebarMenu = () => {
  const { close, open } = useSidebar();
  const [searchInput, setSearchInput] = useState("");
  const pathname = usePathname();

  const unitsMenu = useMenu("menu-sidebar-units");
  const bottomMenu = useMenu("menu-sidebar-bottom");

  const topItems = (unitsMenu?.fields?.menuItems ?? [])
    .map(asMenuItem)
    .filter((x): x is Content.MenuItem => Boolean(x));

  const bottomItems = (bottomMenu?.fields?.menuItems ?? [])
    .map(asMenuItem)
    .filter((x): x is Content.MenuItem => Boolean(x));

  const [expandedId, setExpandedId] = useState<string | null>(() => {
    for (const item of topItems) {
      const nested = getNestedMenu(item);
      const subs = (nested?.fields?.menuItems ?? [])
        .map(asMenuItem)
        .filter((x): x is Content.MenuItem => Boolean(x));
      if (
        subs.some((sub) => {
          const href = resolveHref(sub);
          return pathname === href || pathname.startsWith(href + "/");
        })
      ) {
        return item.sys.id;
      }
    }
    return null;
  });

  const toggleSection = (id: string) =>
    setExpandedId((prev) => (prev === id ? null : id));

  return (
    <div className="container h-full pt-8 px-6 space-y-6">
      <div className="flex justify-between items-center">
        <Icon.Logo className="fill-gray-500" />
        <div className="flex items-center space-x-2">
          <button
            className="btn-icon h-10 w-10"
            title="Tillgänglighetsmeny"
            aria-label="Tillgänglighetsmeny"
            onClick={() => open(<SidebarSettings />)}
          >
            <Icon.Accessibility className="fill-gray-500 h-7" />
          </button>
          <ThemeSwitcher className="btn-icon h-10 w-10" />
          <button
            className="btn-icon h-10 w-10"
            onClick={close}
            aria-label="Stäng meny"
          >
            <Icon.Close className="fill-gray-500 h-4" />
          </button>
        </div>
      </div>
      <div className="relative">
        <div className="w-full relative h-16">
          <InputField
            label="Sök"
            type="text"
            placeholder="Sök efter innehåll"
            containerClassName="h-16 absolute right-0 w-full"
            className="h-full rounded-sm pr-[60px]"
            onChange={(e: any) => setSearchInput(e.target.value)}
            onKeyUpCapture={(e: any) => {
              if (e.key === "Enter") {
                close();
                window.location.href = `/sok?q=${searchInput}`;
              }
            }}
            value={searchInput}
            name="search"
          />
          <Link
            onClick={() => close()}
            href={`/sok?q=${searchInput}`}
            className="btn btn-primary h-12 flex items-center absolute right-2 top-2 z-10 font-bold"
          >
            Sök
          </Link>
        </div>
      </div>

      <div className="space-y-6 pb-6">
        {topItems.map((item, i) => {
          const href = resolveHref(item);
          const nested = getNestedMenu(item);
          const subItems = (nested?.fields?.menuItems ?? [])
            .map(asMenuItem)
            .filter((x): x is Content.MenuItem => Boolean(x));

          return (
            <div
              key={item.sys.id}
              className={`space-y-1 border-sand-600  pb-4 ${
                i !== topItems.length - 1 ? "border-b" : ""
              }`}
            >
              <div className="flex justify-between">
                <Link className="" href={href} onClick={() => close()}>
                  <h2 className="inline-block font-noto-sans text-black ">
                    <CustomCategory
                      color={item.fields.color as Content.CategoryColors}
                      name={item.fields.label}
                      size="md"
                      uppercase={false}
                    />
                  </h2>
                </Link>

                <button
                  onClick={() => toggleSection(item.sys.id)}
                  className={`btn-icon mr-1${subItems.length === 0 ? " invisible pointer-events-none" : ""}`}
                  aria-expanded={
                    subItems.length > 0 ? expandedId === item.sys.id : undefined
                  }
                >
                  <Icon.AngleRight
                    className={`h-4 ml-0.5 fill-gray-300 transition-transform duration-200${
                      expandedId === item.sys.id ? " rotate-90" : ""
                    }`}
                  />
                </button>
              </div>
              {subItems.length > 0 && (
                <div
                  className={`grid transition-[grid-template-rows] duration-200 ease-out ${
                    expandedId === item.sys.id
                      ? "grid-rows-[1fr]"
                      : "grid-rows-[0fr]"
                  }`}
                >
                  <ul className="grid grid-cols-2 gap-y-3 ml-4 overflow-hidden">
                    {subItems.map((sub) => (
                      <li key={sub.sys.id}>
                        <ActiveLink
                          href={resolveHref(sub)}
                          onClick={close}
                          activeClassName="!font-bold"
                        >
                          {sub.fields.label}
                        </ActiveLink>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div>
        <ul className="space-y-2">
          {bottomItems.map((link) => (
            <li key={link.sys.id}>
              <Link
                onClick={() => close()}
                className="mb-3 pb-3 block border-b border-sand-700 "
                href={resolveHref(link)}
              >
                {link.fields.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default SidebarMenu;
